<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { formatUnixDate } from 'shared/helpers/DateHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  messages: { type: Array, default: () => [] },
});

const { t } = useI18n();
const isLoading = useMapGetter('supportTickets/getSupportMessagesLoading');

const scrollContainer = ref(null);

const sortedMessages = computed(() =>
  [...props.messages].sort((a, b) => a.createdAt - b.createdAt)
);

const senderName = message => {
  if (message.isPlatformReply === true) {
    return t('SUPPORT_TICKETS.PLATFORM_SUPPORT');
  }
  return message.sender?.name;
};

const scrollToBottom = async () => {
  await nextTick();
  if (scrollContainer.value) {
    scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
  }
};

watch(
  () => props.messages.length,
  () => scrollToBottom(),
  { immediate: true }
);
</script>

<template>
  <div
    ref="scrollContainer"
    class="flex flex-col flex-1 min-h-0 gap-4 px-4 py-4 overflow-y-auto"
  >
    <div v-if="isLoading" class="flex justify-center py-8">
      <Spinner />
    </div>
    <p
      v-else-if="!sortedMessages.length"
      class="py-8 text-sm text-center text-n-slate-11"
    >
      {{ t('SUPPORT_TICKETS.NO_MESSAGES') }}
    </p>
    <div v-for="message in sortedMessages" :key="message.id" class="flex gap-3">
      <Avatar
        :name="senderName(message)"
        :src="message.sender?.thumbnail"
        :size="32"
        rounded-full
      />
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ senderName(message) }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ formatUnixDate(message.createdAt) }}
          </span>
        </div>
        <p class="mt-1 text-sm whitespace-pre-wrap break-words text-n-slate-12">
          {{ message.content }}
        </p>
      </div>
    </div>
  </div>
</template>
